/**
 * Override outcomes ▸ By run — the pure part (issue 226, giro 3 «Dopo» points 6 and 7; ADR 9021
 * and 3002 amendments 2026-10-07): per queue of the chosen cycle run, how planners decided — the
 * decision summary's override figures (bata-platform ADR 6036, v0.49), read as `overrideReading`
 * reads them. Nothing is computed here but the run's total over the queues that publish a rate,
 * with the platform's own definition (overridden over decided; absent, never 0 %, when nothing
 * was decided).
 */

import type { DecisionSummary } from '@/api/generated';

import { overrideReading, OVERRIDE_BAND, type OverrideReading } from './benefits';

/** The three queues the decision summary carries override figures for, in reading order. */
export const DECISION_QUEUES = [
  { id: 'proposals', labelKey: 'overview.decided.proposals', field: 'proposals_override' },
  { id: 'transfers', labelKey: 'overview.decided.transfers', field: 'transfers_override' },
  { id: 'priceSteps', labelKey: 'overview.decided.priceSteps', field: 'price_actions_override' },
] as const;

/** The band's ends in percent, for the catalogue's `{low}`/`{high}`. */
export const BAND_VALUES = { low: OVERRIDE_BAND.low * 100, high: OVERRIDE_BAND.high * 100 } as const;

export interface QueueOverrideRow {
  readonly id: (typeof DECISION_QUEUES)[number]['id'];
  readonly labelKey: string;
  readonly reading: OverrideReading;
}

/** One row per queue, from the run's decision summary. */
export function queueOverrideRows(summary: DecisionSummary): readonly QueueOverrideRow[] {
  return DECISION_QUEUES.map((queue) => ({ id: queue.id, labelKey: queue.labelKey, reading: overrideReading(summary[queue.field]) }));
}

export interface RunOverrideTotals {
  /** Whether any queue publishes its override figures (a platform before v0.49 publishes none). */
  readonly published: boolean;
  readonly decided: number;
  readonly overridden: number;
  /** Overridden over decided; `null` when nothing was decided. */
  readonly rate: number | null;
}

/** The run's total over the queues that publish figures. */
export function runOverrideTotals(rows: readonly QueueOverrideRow[]): RunOverrideTotals {
  let decided = 0;
  let overridden = 0;
  for (const row of rows) {
    if (row.reading.kind === 'rate') {
      decided += row.reading.decided;
      overridden += row.reading.overridden;
    }
  }
  return {
    published: rows.some((row) => row.reading.kind !== 'notPublished'),
    decided,
    overridden,
    rate: decided === 0 ? null : overridden / decided,
  };
}
